// Added by Sejune Oh on 2019-02-19
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import moment from 'moment'
import numeral from 'numeral'
import validator from 'validator'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Intl from './Intl'
import { SORT_OPTION, KeyIndexedObject, numericValidatorProps, InputValidator, GetQueryParams } from './define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 *  Contents
 *
 * [01] Cookie
 * [02] Get browser language preference
 * [03] Sorting
 * [04] url query
 * [05] Get id list
 * [06] Get Date with format
 * [07] Get Form Data
 * [08] Get Disk Size from Mib
 * [09] Get Mgmt Paths
 * [10] Config Form Utils
 * [11] Get Milliseconds
 * [12] Get random key
 * [13] Get CSV Blob
 * [14] Download file by streamsaver
 * [15] Get container element for adding web component
 * [16] Parsing logs
 * [17] Get task execution time
 * [18] Copy to Clipboard
 * [19] Get Studylist time (ignore timezone)
 * [20] Check not number special character
 * 
 */

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [01] Cookie
// Cookie funtions parameter
interface ICookieParams {
  name?: string
  value?: string
  expHours?: number
}

export const setCookie = ({ name, value, expHours = 0 }: ICookieParams) => {
  const date = new Date()
  date.setTime(date.getTime() + expHours * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`
}

export const getCookie = (name: string) => {
  const value = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)')
  return value ? value[2] : null
}

// Modify cookie on browser
export const updateCookie = ({ name, value, expHours = 0 }: ICookieParams) => {
  const tempCookie = getCookie(name as string)
  if (tempCookie) {
    deleteCookie(name as string)
    setCookie({ name, value, expHours })
  }
}

// Delete cookie on browser
export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [02] Get browser language preference
export const getDefaultLang = () => {
  let lang =
    (navigator.languages && navigator.languages[0]) || // Chrome / Firefox
    navigator.language || // All browsers
    // navigator.userLanguage || // IE <= 10
    'en-US'

  const key = lang.split('-')[0].toLowerCase()

  switch (key) {
    case 'ko':
      return 'ko-KR'
    case 'en':
    default:
      return 'en-US'
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [03] Sorting
export const getSortList = (array: Array<any>, order: SORT_OPTION, orderBy: string) => {
  const stabilizedList = array.map((el: any, index: number) => [el, index])
  const comporator = getComporator(order, orderBy)

  return stabilizedList
    .sort((a: any, b: any) => {
      const order = comporator(a[0], b[0])
      return order === 0 ? a[1] - b[1] : order
    })
    .map((el: any) => el[0])
}
const getComporator = (order: SORT_OPTION, orderBy: string) => {
  return (a: any, b: any) => (order === 'desc' ? 1 : -1) * getDesc(a, b, orderBy)
}

const getDesc = (a: any, b: any, orderBy: string) => {
  let aValue = a[orderBy]
  let bValue = b[orderBy]

  if (typeof aValue === 'string') {
    aValue = aValue.toLowerCase()
  }

  if (typeof bValue === 'string') {
    bValue = bValue.toLowerCase()
  }

  return bValue < aValue ? -1 : bValue === aValue ? 0 : 1
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [04] URL Query

// # Parse Search Query
export const parseUrlQuery = (query: string, page_size?: number) => {
  if (!!query) {
    let rtnQuerySet: {
      [key: string]: string | number | SORT_OPTION
    } = {}

    query
      .slice(1, query.length)
      .split('&')
      .map((querySet) => {
        const [key, val] = querySet.split('=')
        rtnQuerySet[key as string] = val
        return querySet
      })

    return rtnQuerySet
  }

  if (typeof page_size !== 'undefined') return { page_size }

  return undefined
}

// # Get query path
export const getQueryPath = ({ pageIndex = 0, pageSize, order = 'desc', orderBy, filter }: GetQueryParams): string => {
  let queries: Array<string> = []
  queries.push(`page_index=${pageIndex + 1}`)

  if (pageSize) {
    queries.push(`page_size=${pageSize}`)
  }

  if (orderBy) {
    queries.push(`order_by=${orderBy}:${order}`)
  }

  if (filter) {
    queries.push(`filter=${filter}`)
  }

  return queries.join('&')
}

/**
 * ! KeyValuePair에서 Query string을 만드는 함수.
 * @param query : string으로 만들어줄 KeyValuePair
 */
export const getQueryString = <T>(query: T) => {
  const queryChunk = []
  for (let field in query) {
    const val = query[field]

    if (Array.isArray(val)) {
      for (const v of val) {
        queryChunk.push(`${field}=${v}`)
      }
    } else {
      queryChunk.push(`${field}=${val}`)
    }
  }

  return queryChunk.length === 0 ? '' : '?' + queryChunk.join('&')
}

/**
 * ! Get base url from env
 */
export const getBaseUrl = () => {
  let url = process.env.REACT_APP_API_URL || ''
  if (url.indexOf('http') !== 0) {
    url = `${window.location.origin}/${url}`
  }
  return url
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [05] Get id list
export const getIdList = <T>(list: Array<T>): Array<string> => {
  return list.map((listElement: any) => listElement.id)
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [06] Get date with format
export const getDateWithFormat = (date: string, dateOnly: boolean = false, timeOnly: boolean = false, format?: string): string => {
  if (new Date(date).getFullYear() < 1900) return '-'
  if (format) return moment(date).format(format)
  if (dateOnly) return moment(date).format('YYYY-MM-DD')
  if (timeOnly) return moment(date).format('HH:mm:ss')
  return moment(date).format('YYYY-MM-DD HH:mm:ss')
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [07] Get Form Data
export const getFormData = <T>(form: HTMLFormElement, force = false): T | undefined => {
  const nodes = form.querySelectorAll('input')
  const element: KeyIndexedObject<string | boolean | number> = {}

  for (let i = 0, max = nodes.length; i < max; i++) {
    const node = nodes[i]
    const nodeType = node.getAttribute('itemtype') || node.getAttribute('type')
    const isRequired = node.getAttribute('required') !== null
    const pNode = node.parentNode as HTMLElement
    const nodeValue = getValueByInputType(nodeType as string, node)

    if (!force) {
      if (pNode.dataset.error === 'true' || node.dataset.error === 'true') {
        throw getInputErrMessage(pNode)
      }

      if (isRequired && validator.isEmpty(nodeValue as string, { ignore_whitespace: true })) {
        const eMessage = Intl.getIntlText({ id: 'htmljs_react.global.util.msg_required' })
        throw new Error(eMessage)
      }
    }

    if (!!node.id) element[node.id] = nodeValue
  }

  return element as any
}

const getValueByInputType = (type: string, node: HTMLInputElement): string | boolean | number => {
  switch (type) {
    case 'checkbox':
      return node.checked
    case 'boolean':
      return node.value === 'true' || node.value === 'yes'
    default:
      return node.value
  }
}

const getInputErrMessage = (node: HTMLElement) => {
  const pNode = node.parentElement as HTMLElement
  const pNodeSibling = pNode.nextElementSibling as HTMLElement
  return new Error(pNodeSibling.dataset.text || ('Error' as string))
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [08] Get Disk Size from Mib
export const getDiskSizeFromMib = (size: number = 0) => {
  return numeral(size * 1000000).format('0.00 ib')
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//  [09] Get Mgmt Paths
export const getManagemnetPaths = (path: string) => {
  const [, mainPath, subPath, taskPath] = path.split('/')
  return { mainPath, subPath, taskPath, fullPath: `/${mainPath}/${subPath}/` }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [10] Config Form Utils
export const floatValidatorWithOptions = (options: numericValidatorProps, value: number | string) => {
  if (!validator.isFloat(value.toString(), { ...options })) {
    const min = typeof options.min === 'undefined' ? (typeof options.gt === 'undefined' ? 0 : options.gt) : options.min
    const max = typeof options.max === 'undefined' ? (typeof options.lt === 'undefined' ? 0 : options.lt) : options.max
    return [true, Intl.getIntlTextWithOption({ id: 'htmljs_react.global.util.msg_value_between' }, { min, max })]
  }

  return [false]
}

export const intValidatorWithOptions = (options: numericValidatorProps, value: number | string) => {
  if (!validator.isInt(value.toString())) {
    return [true, Intl.getIntlText({ id: 'htmljs_react.global.util.msg_int_number' })]
  }

  if (!validator.isInt(value.toString(), { ...options })) {
    const min = typeof options.min === 'undefined' ? (typeof options.gt === 'undefined' ? 0 : options.gt) : options.min
    const max = typeof options.max === 'undefined' ? (typeof options.lt === 'undefined' ? 0 : options.lt) : options.max

    return [true, Intl.getIntlTextWithOption({ id: 'htmljs_react.global.util.msg_value_between' }, { min, max })]
  }

  return [false]
}

export const parseRange = (isInt: boolean, range: string, rangeKey = 'X') => {
  const [minRange, maxRange] = range.split(',')
  let options: numericValidatorProps = {
    range: rangeKey,
  }

  if (minRange) {
    const sign = minRange[0]
    const value = isInt ? parseInt(minRange.slice(1, minRange.length)) : parseFloat(minRange.slice(1, minRange.length))
    if (sign === '(') {
      options.gt = value
      options.range = `${value} < ${options.range}`
    } else {
      options.min = value
      options.range = `${value} ≤ ${options.range}`
    }
  }

  if (maxRange) {
    const sign = maxRange[maxRange.length - 1]
    const value = isInt
      ? parseInt(maxRange.slice(0, maxRange.length - 1))
      : parseFloat(maxRange.slice(0, maxRange.length - 1))
    if (sign === ')') {
      options.lt = value
      options.range += ` < ${value}`
    } else {
      options.max = value
      options.range += ` ≤ ${value}`
    }
  }

  return options
}

export const getInputOptions = (isInt: boolean, range: string, rangeKey = 'X') => {
  let inputOptions: {
    validator?: InputValidator
    condition?: string
    min?: number
    max?: number
    step?: string
  } = {}

  const options = parseRange(isInt, range, rangeKey)
  if (options.min) inputOptions.min = options.min
  if (options.max) inputOptions.max = options.max

  if (isInt) {
    inputOptions.condition = `(int) ${options.range}`
    inputOptions.validator = intValidatorWithOptions.bind(null, options) as InputValidator
  } else {
    inputOptions.condition = `(real) ${options.range}`
    inputOptions.step = '0.1'
    inputOptions.validator = floatValidatorWithOptions.bind(null, options) as InputValidator
  }

  return inputOptions
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [11] Get milliseconds
export const getMilliSeconds = ({ time, unit = 'm', defaultTime = 1000 * 60 * 15 }: { time: number; unit: 'h' | 'm' | 's', defaultTime: number }) => {
  if (!time) return defaultTime
  if (time < 0) return defaultTime

  let tempTime = null

  switch (unit) {
    case 's':
      tempTime = 1000 * time
      break
    case 'm':
      tempTime = 1000 * 60 * time
      break
    case 'h':
      tempTime = 1000 * 60 * 60 * time
      break
  }

  return tempTime || defaultTime
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [12] Get random key
export const getRandomKey = () => {
  return (
    Math.random()
      .toString(36)
      .substring(2, 16) +
    Math.random()
      .toString(36)
      .substring(18, 35)
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [13] Get CSV Blob
interface GetCSVBlob {
  headerColumn: string[]
  datas: any[]
  displableHeaderColumn?: KeyIndexedObject<string>
}
export const getCSVBlob = ({ headerColumn, displableHeaderColumn, datas }: GetCSVBlob) => {
  const headerColumnList = displableHeaderColumn
    ? headerColumn.map((hc) => displableHeaderColumn[hc] || hc)
    : headerColumn
  let rows = ''
  datas.map((rowData) => {
    headerColumn.map((key, index) => {
      if (index === 0) return (rows += `${rowData[key]}`)
      return (rows += `,${rowData[key]}`)
    })

    rows += '\n'
    return rowData
  })

  return new Blob([`\ufeff${headerColumnList.join(',')}\n` + rows], { type: 'text/csv;charset=EUC-KR;' })
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [14] Download file
export const downloadFile = async (url: string, fileName: string) => {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName

  document.body.appendChild(anchor)
  anchor.click()

  setTimeout(() => {
    document.body.removeChild(anchor)
  }, 0)
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [15] Get container element for adding web component
export function getWebComponentContainer(this: HTMLElement) {
  // https://github.com/facebook/react/issues/9242
  const shadow = this.attachShadow({ mode: 'closed' })
  const container = shadow.appendChild(document.createElement('div'))

  Object.defineProperty(container, 'ownerDocument', { value: shadow })
  // @ts-ignore
  shadow.createElement = (...args) => document.createElement(...args)
  // @ts-ignore
  shadow.createElementNS = (...args) => document.createElementNS(...args)
  // @ts-ignore
  shadow.createTextNode = (...args) => document.createTextNode(...args)

  return container
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [16] Parsing logs
interface ParsedLogs {
  date?: string
  time?: string
  pid?: string
  logLevel?: LogLevel
  message?: string
}

type LogLevel = 'INFO' | 'ERRO' | 'WARN' | 'TRAC' | 'DEBG' | 'FATL'

export const parseLogs = (line: string): ParsedLogs => {
  const lineChunks = line.split(' ')
  const result: ParsedLogs = {}
  const messageStack: string[] = []
  let spaceBuffer = ''

  lineChunks.map((chunk, index) => {
    if (!chunk) {
      spaceBuffer += '\xa0'
      return index
    }

    // ! Date
    if (/^[1-2]\d{3}-[0-1][1-9]-[0-3][0-9]$/.exec(chunk)) {
      result.date = chunk
      return index
    }
    if (/^[1-2]\d{3}[0-1][1-9][0-3][0-9]$/.exec(chunk)) {
      result.date = chunk
      return index
    }

    // ! Time
    if (/\d{2}:\d{2}:\d{2}/.exec(chunk)) {
      result.time = chunk
      return index
    }

    // ! PID
    if (/#\d+/.exec(chunk)) {
      result.pid = chunk
      return index
    }

    // ! Log level
    if (/^(INFO|ERROR|WARN|WARNING|TRACE|DEBUG|FATAL)$/.exec(chunk)) {
      switch (chunk) {
        case 'ERROR':
          result.logLevel = 'ERRO'
          return index
        case 'FATAL':
          result.logLevel = 'FATL'
          return index
        case 'DEBUG':
          result.logLevel = 'DEBG'
          return index
        case 'TRACE':
          result.logLevel = 'TRAC'
          return index
        case 'WARNING':
          result.logLevel = 'WARN'
          return index
        default:
          result.logLevel = chunk as LogLevel
          return index
      }
    }

    messageStack.push(spaceBuffer + chunk)
    spaceBuffer = ''
    return index
  });

  result.message = messageStack.join(' ')
  return result
}

// ! Parse numeric string vales from string array
export const parseNumeric = (messageStack: string[]): string => {
  const numericRegex = /\d+/g
  return messageStack
    .map(message => {
      let lastIndex = 0
      let matched: RegExpExecArray | null
      let rtnLine = ''

      // 현재 Line에서 더 이상 numeric value가 없을때 까지 루프
      while ((matched = numericRegex.exec(message)) !== null) {
        // numeric value가 아닌 문자를 이어 붙임.
        if (matched.index !== 0) rtnLine += message.slice(lastIndex, matched.index)

        rtnLine += `<span data-type='nemeric'>${message.slice(matched.index, numericRegex.lastIndex)}</span>`
        lastIndex = numericRegex.lastIndex
      }

      if (lastIndex !== message.length) {
        rtnLine += message.slice(lastIndex, message.length);
      }

      return rtnLine
    })
    .join(' ')
    .trim()
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [17] Get task execution time
export const getTaskExecutionTime = (startedAt?: string, completedAt?: string) => {
  if (!startedAt || !completedAt) return '-'

  if (new Date(startedAt).getFullYear() < 1900) return '-'

  let executionTime = new Date(completedAt).getTime() - new Date(startedAt).getTime()

  if (executionTime < 0) return '-'
  if (executionTime === 0) return '00:00:00'

  const hour = Math.floor(executionTime / (1000 * 60 * 60))
  executionTime -= hour * 1000 * 60 * 60

  const min = Math.floor(executionTime / (1000 * 60))
  executionTime -= min * 1000 * 60

  const sec = Math.floor(executionTime / 1000)
  return (hour + '').padStart(2, '0') + ':' + (min + '').padStart(2, '0') + ':' + (sec + '').padStart(2, '0')
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [18] Copy to Clipboard
export const clipboardCopy = (el: HTMLInputElement | HTMLTextAreaElement) => {
  el.select()
  document.execCommand('copy')
  // @ts-ignore
  if (window.getSelection) window.getSelection().removeAllRanges()
  // @ts-ignore
  else if (document.selection) document.selection.empty()
  el.blur()
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [19] Get Studylist time (ignore timezone) - `cl-ctrl-behavior.html`과 동일한 함수명 사용
export const convDateTimeString_ignoreTZ = (serverDateString: string) => {
  const serverDate = new Date(serverDateString)
  const date = new Date(serverDate.getTime() + serverDate.getTimezoneOffset() * 60000)
  
  const yyyy = date.getFullYear().toString().padStart(4, '0')
  const mm = (date.getMonth() + 1).toString().padStart(2, '0') // getMonth() is zero based
  const dd = date.getDate().toString().padStart(2, '0')
  
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  
  return `${yyyy}-${mm}-${dd} ${hours}:${minutes}:${seconds}`
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// [20] Check not number special character
export const checkNotNumberSpecialCharacter = (e: any) => {
  const preventArr = ['e', 'E', '.', '+', '-']
  if (preventArr.includes(e.key)) e.preventDefault()
}